// QBR Metrics admin doc — read-only reference for how each metric is
// computed. Keeps the math + data sources visible to admins without
// digging through fmxData.js / metrics.js.
//
// Source of truth: src/qbr/fmxData.js and src/qbr/metrics.js. When
// the formula changes there, update the matching METRIC_DOCS entry
// below so this view doesn't drift.

const NAVY    = '#041662';
const ORANGE  = '#CF4A12';
const GREY    = '#6B7280';
const BORDER  = '#E5E7EB';
const CODE_BG = '#F3F4F6';
const TINT    = '#FAFBFC';

const METRIC_DOCS = [
  {
    key: 'completion_rate',
    label: 'Maintenance Activity',
    summary: 'Share of work requests created in the period that were successfully closed.',
    formula: 'finalized_WRs ÷ total_WRs_in_period × 100',
    source: '/v1/<slug>-requests — counts rows with status === "Finalized"',
    notes: [
      'Declined and Deleted are terminal states but NOT successful completions, so they aren\'t counted as completed.',
      'The denominator is every WR with createdTimeUtc inside the quarter — open WRs included.',
    ],
  },
  {
    key: 'response_time',
    label: 'Team Performance',
    summary: 'Three timing series — first response, first assignment, and resolution — each as median + mean (+ mode for the future graph).',
    formula: [
      'response   = createdTimeUtc → first response action (comment), OR resolutionTimeUtc if no comment was left',
      'assignment = createdTimeUtc → assignedTimeUtc',
      'resolution = createdTimeUtc → resolutionTimeUtc',
    ].join('\n'),
    source: [
      'WR fields: createdTimeUtc, assignedTimeUtc, resolutionTimeUtc',
      'Actions: /v1/<slug>-requests/{id}?fields=:default,actions — filtered to kind === "response"',
    ].join('\n'),
    notes: [
      'Response time is GRADED. Assignment time is auxiliary (shows how fast tickets are picked up); it isn\'t in the rubric.',
      'Mode is bucketed by whole days because exact-millisecond deltas would never repeat — bucketed mode is the meaningful "most common turnaround".',
      'Actions are fetched inline when FMX returns them on the list endpoint, otherwise per-WR in parallel batches of 5 (only for in-period WRs).',
    ],
  },
  {
    key: 'planned_vs_reactive',
    label: 'Planned vs Reactive',
    summary: 'Ratio of completed planned work to completed reactive work.',
    formula: 'completed_PMs ÷ (completed_PMs + finalized_WRs) × 100',
    source: [
      'PMs: /v1/<slug>/occurrences — status === "Completed"',
      'WRs: /v1/<slug>-requests — status === "Finalized"',
      'Only modules with BOTH a work-request and a work-task slug contribute.',
    ].join('\n'),
    notes: [
      'Counts only successfully completed work — Declined/Deleted WRs and Skipped/Retired PMs are excluded.',
      'A module that has only WRs (no PM module on the same slug) doesn\'t contribute to this ratio.',
    ],
  },
  {
    key: 'pm_compliance',
    label: 'PM Compliance',
    summary: 'Two completion rates for PM occurrences scheduled in the period, plus the customer\'s committed target.',
    formula: [
      'on-time   = completed_PMs_with_executionTimeUtc_≤_date ÷ PMs_with_a_due_date × 100',
      'any-time  = completed_PMs ÷ total_PMs_scheduled_in_period × 100',
    ].join('\n'),
    source: [
      '/v1/<slug>/occurrences — uses `date` (scheduled day) as the implicit due date and `executionTimeUtc` as the actual completion.',
      'Target: project.card_settings.QBR.pmTarget — set per project in Settings → QBR Settings.',
    ].join('\n'),
    notes: [
      'On-time is GRADED against the target (A: at/above, B: within 5, C: 5–15 below, D/F: more).',
      'Any-time is shown for context but doesn\'t affect the grade.',
    ],
  },
  {
    key: 'backlog_aging',
    label: 'Open Backlog & Aging',
    summary: 'Health of the open queue: how much is significantly overdue, and how old the oldest ticket has gotten.',
    formula: [
      '% overdue 30d = open_WRs_whose_dueDate_was_more_than_30d_before_period_end ÷ total_open_WRs × 100',
      'oldest age     = max( period_end - createdTimeUtc ) across open WRs, in days',
    ].join('\n'),
    source: '/v1/<slug>-requests — open = status NOT in {Finalized, Declined, Deleted, Cancelled, Skipped, Retired}.',
    notes: [
      'Counted across all WRs pulled (not just in-period). An ancient open ticket created two quarters ago counts toward this period\'s backlog health.',
    ],
  },
  {
    key: 'adoption',
    label: 'Adoption & Engagement',
    summary: 'Active-user rates measured against the quarter\'s end date, plus the manually entered mobile %.',
    formula: [
      '30-day = users_with_lastLoginTimeUtc_within_30d_of_period_end ÷ eligible_users × 100',
      '90-day = same with a 90-day window',
      'mobile = manual (not exposed by the FMX public API)',
    ].join('\n'),
    source: '/v1/users — lastLoginTimeUtc + canAuthenticate + status',
    notes: [
      '30-day is GRADED (the stricter bar). 90-day is shown for context — a strong 30/90 spread means the customer is sliding off.',
      'Eligible = canAuthenticate ≠ false AND !isDeleted AND !isContact AND !isUsedByIntegration AND status NOT in {disabled, retired, inactive, deleted, suspended}.',
      'Compound statuses like "Email disabled" still count as eligible — the user can still log in.',
    ],
  },
];

const OVERALL_DOC = {
  label: 'Overall rollup',
  summary: 'Cross-module aggregation displayed in the report\'s "Overall" section + the headline grade.',
  formula: [
    'completion_rate     = Σ completed ÷ Σ total',
    'response_time       = weighted_avg(per-module medians/means) by sample N',
    'backlog_aging       = Σ overdue_30d ÷ Σ open;  max(per-module oldestDays)',
    'planned_vs_reactive = Σ completed_PMs ÷ Σ (completed_PMs + finalized_WRs)',
    'pm_compliance       = weighted on-time + any-time across modules',
    'adoption            = same value (tenant-global)',
    'headline grade      = mean letter grade across all 6 metrics that have values (A=4, B=3, …, F=0)',
  ].join('\n'),
  source: 'Computed in aggregateModules() in src/qbr/fmxData.js.',
  notes: [
    'Adoption is tenant-global so Overall just passes the same number through.',
    'Mode rolls up via weighted day-bucket selection (loses some fidelity vs raw-delta re-bucketing — we don\'t keep raw deltas around).',
  ],
};

export default function QbrMetricsAdminTab() {
  return (
    <div style={{ padding: '20px 28px', overflowY: 'auto', maxHeight: '70vh' }}>
      <div style={{ marginBottom: 18, fontSize: 12, color: GREY, lineHeight: 1.5 }}>
        Read-only reference for how each QBR metric is computed and where its data comes from.
        Numbers in the report come straight from these formulas — when FMXers ask
        "why is my grade a B?", this is the page to point them at.
      </div>

      {METRIC_DOCS.map(m => (
        <MetricDoc key={m.key} doc={m} />
      ))}

      <h3 style={{
        fontSize: 11, fontWeight: 700, color: NAVY, textTransform: 'uppercase',
        letterSpacing: 0.5, margin: '28px 0 10px',
      }}>
        Aggregation
      </h3>
      <MetricDoc doc={OVERALL_DOC} overall />
    </div>
  );
}

function MetricDoc({ doc, overall }) {
  return (
    <div style={{
      border: `1px solid ${BORDER}`, borderRadius: 8,
      marginBottom: 12, background: '#fff', overflow: 'hidden',
    }}>
      <div style={{
        padding: '10px 14px', borderBottom: `1px solid ${BORDER}`,
        background: TINT, display: 'flex', alignItems: 'baseline', gap: 10,
      }}>
        <div style={{ width: 3, height: 14, borderRadius: 2, background: overall ? ORANGE : NAVY }} />
        <div style={{ fontSize: 13, fontWeight: 700, color: NAVY }}>{doc.label}</div>
      </div>
      <div style={{ padding: '12px 14px', display: 'grid', gap: 10, fontSize: 12, color: '#374151', lineHeight: 1.5 }}>
        <div>{doc.summary}</div>
        <DocSection label="Formula" mono content={doc.formula} />
        <DocSection label="Source" content={doc.source} />
        {doc.notes && doc.notes.length > 0 && (
          <div>
            <SectionLabel>Notes</SectionLabel>
            <ul style={{ margin: '4px 0 0', paddingLeft: 18, display: 'grid', gap: 4 }}>
              {doc.notes.map((n, i) => <li key={i}>{n}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function DocSection({ label, content, mono }) {
  return (
    <div>
      <SectionLabel>{label}</SectionLabel>
      <pre style={{
        margin: '4px 0 0', padding: '8px 10px',
        background: CODE_BG, borderRadius: 4,
        fontSize: mono ? 11.5 : 12, lineHeight: 1.55,
        fontFamily: mono ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : 'inherit',
        whiteSpace: 'pre-wrap', wordBreak: 'break-word',
        color: '#1F2937',
      }}>{content}</pre>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, color: GREY,
      textTransform: 'uppercase', letterSpacing: 0.4,
    }}>{children}</div>
  );
}
